import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { Module } from "@/constants";
import { useTranslation } from "react-i18next";

type MutationResponse<TData> = TData & { id?: string; queueId?: string };

interface CreateMutationOptions<TData> {
  type: "create";
  response: MutationResponse<TData>;
  path: string;
  successMessage?: string;
}

interface UpdateMutationOptions<TData> {
  type: "update";
  response: MutationResponse<TData>;
  input: TData;
  path?: string;
  successMessage?: string;
}

type MutationOptions<TData> =
  | CreateMutationOptions<TData>
  | UpdateMutationOptions<TData>;

/**
 * A unified hook for handling both create and update mutations with offline support.
 *
 * This hook provides consistent behavior for handling successful mutations whether
 * the user is online or offline. Operations are explicitly typed to avoid ambiguity.
 *
 * @template TData - The data type for the entity being mutated
 * @param moduleId - The module identifier for cache management
 *
 * @example
 * ```typescript
 * const handleMutation = useOfflineAwareMutation<User>(Module.USERS);
 *
 * // For create operations
 * await handleMutation({
 *   type: "create",
 *   response: newUser,
 *   path: "/users",
 *   successMessage: "User created successfully"
 * });
 *
 * // For update operations
 * await handleMutation({
 *   type: "update",
 *   response: updatedUser,
 *   input: originalInput,
 *   path: "/users",
 *   successMessage: "User updated successfully"
 * });
 * ```
 */
export const useOfflineAwareMutation = <TData extends object>(
  moduleId: Module,
) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { updateQueryDataForExistingItem, invalidateListQuery } =
    useQueryCacheUpdater(moduleId);

  /**
   * Handle successful mutation with explicit operation type.
   *
   * **Behavior by scenario:**
   *
   * - **Online Create**: Invalidate cache + navigate to new item + show toast
   * - **Offline Create**: No actions (operation queued, no user feedback)
   * - **Online Update**: Update cache + navigate (if path provided) + show toast
   * - **Offline Update**: Merge data optimistically + update cache (no toast)
   */
  async function handleOfflineAwareMutationSuccess(
    options: MutationOptions<TData>,
  ): Promise<void> {
    const isOfflineOperation = "queueId" in options.response;
    const isOnlineWithResponseId = !isOfflineOperation && options.response.id;

    try {
      if (options.type === "create") {
        if (isOnlineWithResponseId) {
          await invalidateListQuery();
          navigate(`${options.path}/${options.response.id}`);
        }
      } else {
        const dataToCache = isOfflineOperation
          ? { ...options.input, ...options.response } // Merge for offline (response might be partial)
          : options.response; // Use full response for online

        await updateQueryDataForExistingItem(dataToCache);

        // Navigate only if path is provided (optional for updates)
        if (isOnlineWithResponseId && options.path) {
          navigate(`${options.path}/${options.response.id}`);
        }
      }

      // Only show success message when online (operation actually completed)
      if (options.successMessage && !isOfflineOperation) {
        toast({ title: options.successMessage });
      }
    } catch (error) {
      toast({
        title: t("errors.mutationWarning"),
        description: t("errors.mutationWarningDescription"),
        variant: "destructive",
      });
    }
  }

  return handleOfflineAwareMutationSuccess;
};
